import {reconcileResumeTranscript,SessionResumeTranscriptError} from './resumeTranscriptReconciliation';
import type { TimerPauseReason } from './assessmentTimer';
import type { SessionResumeState } from '../lib/api';
import type { TranscriptSpeechAct } from '../services/turnTakingPolicy';

const RESUME_SPEECH_ACTS: ReadonlySet<string> = new Set([
  'question',
  'answer',
  'acknowledgement',
  'bridge',
  'repeat_request',
  'clarification',
  'meta'
]);

function asResumeSpeechAct(value: unknown): TranscriptSpeechAct | undefined {
  return typeof value === 'string' && RESUME_SPEECH_ACTS.has(value)
    ? value as TranscriptSpeechAct
    : undefined;
}

/**
 * Helpers for session resume snapshots (Part A MCQ + Part B oral).
 * Server timer columns are authoritative over autosave payload fields.
 */

export type ResumeTranscriptEntry = {
  model_request_id?: string;
  role: 'ai' | 'candidate';
  content: string;
  timestamp?: string | Date;
  speech_act?: TranscriptSpeechAct;
  original_transcript?: string;
  edited?: boolean;
  edit_source?: string;
  edited_at?: string;
};

export type SessionResumePayload = {
  version?: number;
  phase?: string;
  assessment_phase?: string;
  time_elapsed?: number;
  question_number?: number;
  conversation_count?: number;
  next_question_index?: number;
  current_question?: string;
  ai_message?: string;
  placement_set_id?: string | null;
  transcript?: ResumeTranscriptEntry[];
  remaining_seconds?: number | null;
  active_seconds_consumed?: number | null;
  timer_paused_at?: string | null;
  pause_reason?: string | null;
  mcq?: {
    current_index?: number;
    view_mode?: 'single' | 'list';
    list_filter?: 'all' | 'unanswered' | 'flagged';
    attempt_id?: string | null;
  };
};

export function normalizeResumeTranscript(
  entries: unknown
): Array<{
  role: 'ai' | 'candidate';
  content: string;
  timestamp: Date;
  model_request_id?: string;
  speech_act?: TranscriptSpeechAct;
  original_transcript?: string;
  edited?: true;
  edit_source?: 'candidate_review';
  edited_at?: string;
}> {
  if (!Array.isArray(entries)) return [];

  return entries
    .filter((entry): entry is ResumeTranscriptEntry => Boolean(
      entry
      && typeof entry === 'object'
      && typeof (entry as ResumeTranscriptEntry).content === 'string'
      && String((entry as ResumeTranscriptEntry).content).trim()
    ))
    .map((entry) => ({
      role: entry.role === 'candidate' ? 'candidate' as const : 'ai' as const,
      content: String(entry.content),
      timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
      ...(entry.role === 'ai' && typeof entry.model_request_id === 'string'
        && /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(entry.model_request_id)
        ? {model_request_id:entry.model_request_id.toLowerCase()} : {}),
      ...(asResumeSpeechAct(entry.speech_act) ? { speech_act: asResumeSpeechAct(entry.speech_act) } : {}),
      ...(entry.edited === true && typeof entry.original_transcript === 'string'
        ? {
          original_transcript: entry.original_transcript,
          edited: true as const,
          edit_source: 'candidate_review' as const,
          ...(typeof entry.edited_at === 'string' ? { edited_at: entry.edited_at } : {})
        }
        : {})
    }));
}

/** Prefer first unanswered question; otherwise stay on last answered. */
export function resolveMcqResumeIndex(
  questionIds: string[],
  answers: Record<string, number | null | undefined>
): number {
  if (!questionIds.length) return 0;

  const firstUnanswered = questionIds.findIndex((id) => {
    const value = answers[id];
    return value === null || value === undefined;
  });

  if (firstUnanswered >= 0) return firstUnanswered;
  return Math.max(0, questionIds.length - 1);
}

export function parseResumePayload(raw: unknown): SessionResumePayload | null {
  if (!raw || typeof raw !== 'object') return null;
  return raw as SessionResumePayload;
}

export function hasOralProgress(payload: SessionResumePayload | null | undefined): boolean {
  if (!payload) return false;
  const transcript = normalizeResumeTranscript(payload.transcript);
  const nextIndex = Number(payload.next_question_index);
  const conversationCount = Number(payload.conversation_count);
  return transcript.length > 0
    || (Number.isFinite(nextIndex) && nextIndex > 0)
    || (Number.isFinite(conversationCount) && conversationCount > 0);
}

/** Minimal session slice needed for restore precedence. */
export type SessionTimerSessionSlice = {
  remaining_seconds?: number | null;
  active_seconds_consumed?: number | null;
  timer_paused_at?: string | null;
  pause_reason?: string | null;
};

export type ResolvedTimerRestore = {
  remaining_seconds: number | null;
  active_seconds_consumed: number | null;
  timer_paused: boolean;
  pause_reason: TimerPauseReason;
};

export type ResolvedRestoreState = {
  payload: SessionResumePayload | null;
  timer: ResolvedTimerRestore;
  mcq?: SessionResumePayload['mcq'];
  oral?: {
    time_elapsed: number | null;
    question_number: number | null;
    conversation_count: number | null;
    next_question_index: number | null;
    ai_message: string | null;
    current_question: string | null;
    transcript: ReturnType<typeof normalizeResumeTranscript>;
  };
};

function pickFiniteSeconds(...candidates: Array<number | null | undefined>): number | null {
  for (const value of candidates) {
    if (value === null || value === undefined) continue;
    const n = Number(value);
    if (Number.isFinite(n)) return Math.max(0, Math.floor(n));
  }
  return null;
}

/**
 * Merge server session columns with autosave payload using explicit precedence:
 * timer fields from session columns win; UI position from payload.
 */
export function resolveRestoredSessionState(
  resumeState: SessionResumeState | null | undefined,
  options: { part?: 'part_a' | 'part_b';reconcileTranscript?:boolean } = {}
): ResolvedRestoreState {
  const session = resumeState?.session;
  let payload = parseResumePayload(resumeState?.autosave?.payload);
  if(options.reconcileTranscript){
    if(resumeState?.transcript_reconciliation_version!==1||!resumeState.session?.id)throw new SessionResumeTranscriptError();
    const transcript=reconcileResumeTranscript(resumeState.session.transcript,payload?.transcript,(payload as Record<string,unknown>|null)?.transcript_window);
    payload={...payload,transcript:normalizeResumeTranscript(transcript)};
  }

  const remaining_seconds = pickFiniteSeconds(
    session?.remaining_seconds,
    payload?.remaining_seconds
  );
  const active_seconds_consumed = pickFiniteSeconds(
    session?.active_seconds_consumed,
    payload?.active_seconds_consumed,
    payload?.time_elapsed
  );

  const pauseFromSession = session?.timer_paused_at
    ? (session.pause_reason as TimerPauseReason) || 'disconnect'
    : null;
  const pauseFromPayload = payload?.timer_paused_at
    ? (payload.pause_reason as TimerPauseReason) || 'disconnect'
    : null;

  const timer: ResolvedTimerRestore = {
    remaining_seconds,
    active_seconds_consumed,
    timer_paused: Boolean(session?.timer_paused_at || payload?.timer_paused_at),
    pause_reason: pauseFromSession || pauseFromPayload
  };

  const base: ResolvedRestoreState = {
    payload,
    timer,
    mcq: payload?.mcq
  };

  if (options.part === 'part_b' || payload?.phase === 'oral' || hasOralProgress(payload)) {
    base.oral = {
      time_elapsed: active_seconds_consumed,
      question_number: Number.isFinite(Number(payload?.question_number))
        ? Number(payload?.question_number)
        : null,
      conversation_count: Number.isFinite(Number(payload?.conversation_count))
        ? Number(payload?.conversation_count)
        : null,
      next_question_index: Number.isFinite(Number(payload?.next_question_index))
        ? Number(payload?.next_question_index)
        : null,
      ai_message: typeof payload?.ai_message === 'string' ? payload.ai_message : null,
      current_question: typeof payload?.current_question === 'string' ? payload.current_question : null,
      transcript: normalizeResumeTranscript(payload?.transcript)
    };
  }

  return base;
}
